/**
 * Data Lifecycle Scheduler
 * Runs periodic checks for data that needs archiving or deletion
 * Should be called on app initialization and periodically (e.g., daily)
 */

import { ArchiveService } from '../services/ArchiveService';
import { DataService } from '../services/DataService';

/**
 * Check for and notify about data lifecycle events
 * This should be called when the app loads and periodically
 */
export function checkDataLifecycle(userId: string = 'system'): {
  archiveCandidates: number;
  deletionCandidates: number;
  notifications: string[];
} {
  const notifications: string[] = [];
  const adminUsers = DataService.getUsers().filter((user: any) => (user.role === 'super_admin' || user.role === 'admin') && user.id);
  
  // Scan for archive candidates
  const archiveCandidates = ArchiveService.scanForArchiveCandidates();
  
  // Scan for deletion candidates
  const deletionCandidates = ArchiveService.scanForDeletionCandidates();
  
  // Create notifications for admins
  if (archiveCandidates.length > 0) {
    notifications.push(
      `${archiveCandidates.length} project(s) are ready for archival (4+ years old)`
    );
    
    // Create system notifications for actual admin users
    archiveCandidates.forEach(project => {
      const daysOld = ArchiveService.getAgeInDays(project.createdAt);
      adminUsers.forEach((adminUser: any) => {
        DataService.createNotification({
          userId: adminUser.id,
          type: 'system',
          title: 'Project Ready for Archival',
          message: `Project "${project.name}" is ${daysOld} days old and ready for archival`,
          priority: 'medium',
          read: false,
          relatedEntityId: project.id,
          relatedEntityType: 'project',
        });
      });
    });
  }
  
  if (deletionCandidates.length > 0) {
    notifications.push(
      `${deletionCandidates.length} archived project(s) scheduled for deletion`
    );
    
    // Create high-priority notifications for deletion
    deletionCandidates.forEach(archived => {
      const daysUntilDeletion = Math.ceil(
        (new Date(archived.deletionScheduledAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)
      );
      
      adminUsers.forEach((adminUser: any) => {
        DataService.createNotification({
          userId: adminUser.id,
          type: 'system',
          title: 'Archived Data Pending Deletion',
          message: `Project "${archived.data?.project?.name || 'Unknown'}" will be permanently deleted in ${daysUntilDeletion} days`,
          priority: 'high',
          read: false,
          relatedEntityId: archived.entityId,
          relatedEntityType: 'project',
        });
      });
    });
  }
  
  return {
    archiveCandidates: archiveCandidates.length,
    deletionCandidates: deletionCandidates.length,
    notifications,
  };
}

/**
 * Automatically archive old projects (if auto-archive is enabled)
 * Returns number of projects archived
 */
export function runAutoArchive(userId: string = 'system'): number {
  const policy = ArchiveService.getDefaultPolicy();
  
  if (!policy.autoArchive) {
    return 0;
  }
  
  const count = ArchiveService.autoArchiveOldProjects(userId);
  
  return count;
}

/**
 * Get summary of data lifecycle status
 */
export function getLifecycleSummary() {
  const stats = ArchiveService.getStorageStats();
  const archiveCandidates = ArchiveService.scanForArchiveCandidates();
  const deletionCandidates = ArchiveService.scanForDeletionCandidates();
  
  return {
    ...stats,
    archiveCandidatesCount: archiveCandidates.length,
    deletionCandidatesCount: deletionCandidates.length,
    healthStatus: getHealthStatus(stats),
  };
}

/**
 * Determine database health based on storage stats
 */
function getHealthStatus(stats: any): 'excellent' | 'good' | 'warning' | 'critical' {
  const archivedRatio = stats.archivedProjects / stats.totalProjects;
  
  if (archivedRatio < 0.1) return 'excellent'; // Less than 10% archived
  if (archivedRatio < 0.3) return 'good'; // Less than 30% archived
  if (archivedRatio < 0.5) return 'warning'; // Less than 50% archived
  return 'critical'; // More than 50% archived
}

/**
 * Initialize lifecycle checking on app start
 */
export function initializeDataLifecycle() {
  // Check lifecycle status silently
  const check = checkDataLifecycle();
  
  // Get summary
  const summary = getLifecycleSummary();
  
  // Schedule periodic checks (every 24 hours)
  const checkInterval = 24 * 60 * 60 * 1000; // 24 hours
  setInterval(() => {
    checkDataLifecycle();
  }, checkInterval);
  
  return summary;
}
